// Shared Blog Service
// This ensures both the blog page and admin panel use the same data

// Support both CRA and Vite environment variables
const getApiBase = () => {
  // Try Vite environment variables first
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_REACT_APP_PUBLIC_URL) {
    return import.meta.env.VITE_REACT_APP_PUBLIC_URL.replace(/\/$/, '');
  }
  // Default fallback
  return "https://itcsante-backend.onrender.com";
};

const API_BASE = getApiBase();

// No mock data needed - using real API

class BlogService {
  constructor() {
    // Always use real API, no mock mode
    this.useMockAPI = false;
  }

  // Get all blogs (for both frontend and admin)
  async getBlogs() {
    try {
      const response = await fetch(`${API_BASE}/api/blog`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Backend returns { blogs: [...], pagination: {...} } or just array
      return data.blogs || data;
    } catch (error) {
      console.error('Error fetching blogs:', error);
      // Return empty array if API fails
      return [];
    }
  }

  // Get published blogs only (for frontend blog page)
  async getPublishedBlogs() {
    const blogs = await this.getBlogs();
    return blogs.filter(blog => blog.published === true);
  }

  // Get single blog by ID
  async getBlogById(id) {
    try {
      const response = await fetch(`${API_BASE}/api/blog/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching blog:', error);
      return null;
    }
  }

  // Create new blog (admin only)
  async createBlog(blogData) {
    try {
      const response = await fetch(`${API_BASE}/api/blog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(blogData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating blog:', error);
      throw error;
    }
  }

  // Update blog (admin only)
  async updateBlog(id, blogData) {
    try {
      const response = await fetch(`${API_BASE}/api/blog/${id}`, {
        method: 'PATCH', // Changed from PUT to PATCH to match backend
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(blogData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating blog:', error);
      throw error;
    }
  }

  // Delete blog (admin only)
  async deleteBlog(id) {
    try {
      const response = await fetch(`${API_BASE}/api/blog/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting blog:', error);
      throw error;
    }
  }

  // Refresh data (for syncing between tabs)
  refreshData() {
    // No-op since we're using real API
    console.log('Data refresh requested - using real API');
  }
}

// Export singleton instance
export const blogService = new BlogService();
export default blogService;
